import {
  Box,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { formatDateTime } from '../../../utils/dateTimeUtils';

export interface GroupAudit {
  createdDate: string;
  createdUserName?: string;
  updatedDate?: string;
  updatedUserName?: string;
}

interface ActivityGroupSectionProps {
  groupDescription: string;
  onGroupDescriptionChange: (value: string) => void;
  /** Who created/updated the group and when. Present only when editing an existing group — shown as read-only info. */
  audit?: GroupAudit;
  clientTotal: number;
  providerTotal: number;
  paidByClientAmount?: number;
  onPaidByClientAmountChange: (value: number | undefined) => void;
  paidToProviderAmount?: number;
  onPaidToProviderAmountChange: (value: number | undefined) => void;
}

export default function ActivityGroupSection({
  groupDescription,
  onGroupDescriptionChange,
  audit,
  clientTotal,
  providerTotal,
  paidByClientAmount,
  onPaidByClientAmountChange,
  paidToProviderAmount,
  onPaidToProviderAmountChange,
}: ActivityGroupSectionProps) {
  // "??" falls back to 0 (not clientTotal/providerTotal) to match what a submit would send
  // right now (useActivityGroupSubmit also defaults an empty field to 0) — showing "fully
  // paid" here while an empty field would actually save as "fully in debt" would be a lie.
  const clientDebt = clientTotal - (paidByClientAmount ?? 0);
  const providerDebt = providerTotal - (paidToProviderAmount ?? 0);

  return (
    <Paper sx={{ p: 2, mb: 3, backgroundColor: 'background.default', border: '2px solid', borderColor: 'primary.main' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: audit ? 0.5 : 2 }}>
        <Typography variant="h6" color="primary">
          Група активностей
        </Typography>
      </Box>
      {audit && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Створено: {formatDateTime(audit.createdDate)}
            {audit.createdUserName ? ` · ${audit.createdUserName}` : ''}
          </Typography>
          {audit.updatedDate && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Оновлено: {formatDateTime(audit.updatedDate)}
              {audit.updatedUserName ? ` · ${audit.updatedUserName}` : ''}
            </Typography>
          )}
        </Box>
      )}
      <TextField
        fullWidth
        label="Опис групи"
        value={groupDescription}
        onChange={(e) => onGroupDescriptionChange(e.target.value)}
        placeholder="Введіть опис для групи активностей"
        sx={{ mb: clientTotal > 0 || providerTotal > 0 ? 2 : 0 }}
      />

      {clientTotal > 0 && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: providerTotal > 0 ? 2 : 0 }}>
          <TextField
            type="number"
            label="Оплачено клієнтом одразу (USD)"
            value={paidByClientAmount ?? ''}
            onChange={(e) =>
              onPaidByClientAmountChange(e.target.value === '' ? undefined : parseFloat(e.target.value))
            }
            slotProps={{ htmlInput: { min: 0, max: clientTotal, step: 0.01 } }}
          />
          <TextField
            label="Борг клієнта (USD)"
            value={`$${Math.max(clientDebt, 0).toFixed(2)}`}
            slotProps={{ htmlInput: { readOnly: true } }}
            disabled
          />
        </Box>
      )}

      {providerTotal > 0 && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          <TextField
            type="number"
            label="Оплачено постачальнику одразу (USD)"
            value={paidToProviderAmount ?? ''}
            onChange={(e) =>
              onPaidToProviderAmountChange(e.target.value === '' ? undefined : parseFloat(e.target.value))
            }
            slotProps={{ htmlInput: { min: 0, max: providerTotal, step: 0.01 } }}
          />
          <TextField
            label="Борг постачальнику (USD)"
            value={`$${Math.max(providerDebt, 0).toFixed(2)}`}
            slotProps={{ htmlInput: { readOnly: true } }}
            disabled
          />
        </Box>
      )}
    </Paper>
  );
}
